import React from "react";
import { Box, Typography, Button, Divider, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CoinIcon from "../../assets/svgs/CoinIcon";
import onyxLogo from "../../assets/badgets/onyx.png";
import sapphireLogo from "../../assets/badgets/shapfire.png";
import diamondLogo from "../../assets/badgets/diamand.png";
import platinumLogo from "../../assets/badgets/platium.png";
import freemiumLogo from "../../assets/badgets/free.png";

interface SubscriptionPlan {
  tier: "freemium" | "onyx" | "sapphire" | "diamond" | "platinum_vip";
  name: string;
  price: number;
  credits: number;
  perks: string[];
  popular?: boolean;
}

interface SubscriptionPlanCardProps {
  isActive: boolean;
  plan: SubscriptionPlan;
  currentTier?: string;
  currentPlan?: SubscriptionPlan | null;
  onSubscribe: (tier: string) => void;
  onUpgrade: (tier: string) => void;
  discountPercentage?: number;
  loading?: boolean;
}

const useStyles = makeStyles(theme => ({
  card: {
    minHeight: "-webkit-fill-available",
    borderRadius: "16px",
    color: "white",
    background: "#131313",
    padding: "24px 16px",
    position: "relative",
    transition: "all 0.3s ease",
    border: "1px solid #1F1F1F",
    "&:hover": {
      cursor: "pointer",
      transform: "translateY(-8px)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "20px 14px",
    },
  },
  popularCard: {
    border: "2px solid #FE5A97",
    transform: "scale(1.05)",
    "&:hover": {
      transform: "scale(1.05) translateY(-8px)",
    },
  },
  currentCard: {},
  popularBadge: {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "linear-gradient(45deg, #FE5A97, #ff85c1)",
    color: "white",
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  discountChip: {
    position: "absolute",
    right: "-50%",
    top: 10,
    background: "#FE5A97",
    color: "#fff",
    borderRadius: 999,
    padding: "4px 10px",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1,
  },
  cardHeader: {
    gap: "3.5px",
    display: "flex",
    textAlign: "center",
  },
  planName: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#FFFFFF",
    transform: "captilize",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },
  planPrice: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: "4px",
  },
  currency: {
    fontSize: "20px",
    color: "#FE5A97",
    fontWeight: 600,
  },
  amount: {
    fontSize: "36px",
    fontWeight: 700,
    color: "#FE5A97",
    [theme.breakpoints.down("sm")]: {
      fontSize: "32px",
    },
  },
  period: {
    fontSize: "16px",
    color: "#666",
    fontWeight: 400,
  },
  creditsInfo: {
    textAlign: "center",
    marginBottom: "24px",
    padding: "16px",
    background: "#f8f9fa",
    borderRadius: "12px",
    border: "1px solid #e9ecef",
  },
  creditsAmount: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "4px",
  },
  creditsDescription: {
    color: "#666",
    fontSize: "14px",
  },
  benefitsList: {
    listStyle: "none",
    padding: 0,
  },
  benefitItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: "14px",
  },
  benefitIcon: {
    color: "#fff",
    marginRight: "12px",
    fontSize: "18px",
    fontWeight: "bold",
  },
  benefitText: {
    color: "#fff",
    fontSize: "14px",
    lineHeight: 1.4,
  },
  mainCard: {
    color: "#000 !important",
    backgroundColor: "#FFD0E1",
    "& .planName": {
      color: "#000 !important",
    },
    "& .bgPhone": {
      color: "#000 !important",
      backgroundColor: "#BE32651A",
    },
  },
  subscriptionBtn: {
    width: "100%",
    padding: "14px 24px",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginBottom: "16px",
    textTransform: "none",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
    },
    "&:disabled": {
      cursor: "not-allowed",
      transform: "none",
      boxShadow: "none",
    },
  },
  btnCurrent: {
    background: "#4CAF50",
    color: "white",
  },
  btnFreemium: {
    background: "#28a745",
    color: "white",
  },
  btnPopular: {
    background: "linear-gradient(45deg, #FE5A97, #ff85c1)",
    color: "white",
  },
  btnPremium: {
    marginTop: 25,
    color: "white",
    background: "#FE5A97",
  },
  btnDefault: {
    marginTop: 25,
    color: "white",
    background: "trasnparent",
    border: "1px solid #FE5A97",
  },
  planFooter: {
    textAlign: "center",
    "& small": {
      display: "block",
      color: "#999",
      fontSize: "12px",
      marginBottom: "4px",
    },
  },
  tierIcon: {
    marginBottom: "8px",
    display: "flex",
    justifyContent: "center",
  },
}));

const getTierIcon = (tier: string) => {
  switch (tier) {
    case "freemium":
      return (
        <img
          src={freemiumLogo}
          alt="Freemium"
          style={{ width: 32, height: 32 }}
        />
      );
    case "onyx":
      return (
        <img src={onyxLogo} alt="Onyx" style={{ width: 32, height: 32 }} />
      );
    case "sapphire":
      return (
        <img
          src={sapphireLogo}
          alt="Sapphire"
          style={{ width: 32, height: 32 }}
        />
      );
    case "diamond":
      return (
        <img
          src={diamondLogo}
          alt="Diamond"
          style={{ width: 32, height: 32 }}
        />
      );
    case "platinum_vip":
      return (
        <img
          src={platinumLogo}
          alt="Platinum VIP"
          style={{ width: 32, height: 32 }}
        />
      );
    default:
      return (
        <img
          src={freemiumLogo}
          alt="Freemium"
          style={{ width: 32, height: 32 }}
        />
      );
  }
};

const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  isActive,
  plan,
  currentPlan,
  onSubscribe,
  discountPercentage,
  loading = false,
}) => {
  const classes = useStyles();

  const isCurrentPlan = isActive;

  // Визначаємо, чи це upgrade чи downgrade
  const isDowngrade = currentPlan && plan.price < currentPlan.price;
  const isUpgrade = currentPlan && plan.price > currentPlan.price;
  const actionText = isCurrentPlan
    ? "Active"
    : isDowngrade
    ? "Downgrade"
    : isUpgrade
    ? "Upgrade"
    : "Upgrade"; // За замовчуванням "Upgrade"

  const getButtonClass = () => {
    if (plan.tier === "platinum_vip") return classes.btnPremium;
    return classes.btnDefault;
  };

  const handleAction = () => {
    onSubscribe(plan.tier);
  };

  const getCardClass = () => {
    let cardClass = classes.card;
    if (isCurrentPlan) cardClass += ` ${classes.currentCard}`;
    if (plan.popular) cardClass += ` ${classes.popularCard}`;
    if (plan.tier === "platinum_vip") cardClass += ` ${classes.mainCard}`;
    return cardClass;
  };

  return (
    <Box className={getCardClass()}>
      {/* Популярний бейдж */}
      {plan.popular && <Box className={classes.popularBadge}>Most Popular</Box>}

      {/* Заголовок */}
      <Box className={classes.cardHeader}>
        <Box className={classes.tierIcon}>{getTierIcon(plan.tier)}</Box>
        <Typography className={`${classes.planName} planName`}>
          {plan.name}
        </Typography>
      </Box>

      <Box
        className="bgPhone"
        paddingX="14px"
        paddingY="14px"
        bgcolor="#1F1F1F"
        borderRadius="8px"
        color="white"
        marginTop="12px"
        marginBottom="24px"
        display="flex"
        alignItems="center"
        fontSize="20px"
        style={{
          gap: 4,
        }}
      >
        <CoinIcon />{" "}
        <b style={{ fontSize: 24 }}>
          {plan.tier === "freemium" ? "2" : `${plan.credits}`}
        </b>{" "}
        credits/month
      </Box>

      <Box
        display="flex"
        alignItems="flex-end"
        style={{
          gap: 8,
          position: "relative",
        }}
      >
        <Box
          style={{
            gap: 8,
            position: "relative",
          }}
        >
          <Typography
            className="planName"
            style={{
              fontSize: 40,
              fontWeight: 600,
              color: "#fff",
            }}
          >
            ${plan.price}
          </Typography>
          {discountPercentage ? (
            <Box className={classes.discountChip}>-{discountPercentage}%</Box>
          ) : null}
        </Box>
        <Typography
          className="planName"
          style={{
            fontSize: 20,
            color: "#fff",
          }}
        >
          /month
        </Typography>
      </Box>

      <Typography
        className="planName"
        style={{
          fontSize: 16,
          color: "#fff",
        }}
      >
        Auto renewal, cancel anytime
      </Typography>

      {/* Кнопка дії */}
      <Button
        className={`${classes.subscriptionBtn} ${getButtonClass()}`}
        onClick={handleAction}
        disabled={isCurrentPlan || loading}
      >
        {loading ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={20} style={{ color: "#fff" }} />
            Processing...
          </Box>
        ) : (
          actionText
        )}
      </Button>

      {plan.tier !== "freemium" && (
        <Divider
          style={{
            margin: "32px 0",
            backgroundColor: "#7474744D",
          }}
        />
      )}

      {/* Переваги */}
      <ul className={classes.benefitsList}>
        {Array.isArray(plan?.perks)
          ? plan.perks.map((perk, index) => (
              <li key={index} className={classes.benefitItem}>
                <span className={`${classes.benefitIcon} planName`}>✓</span>
                <span className={`${classes.benefitText} planName`}>
                  {perk}
                </span>
              </li>
            ))
          : plan?.perks &&
            Object.entries(plan.perks)
              .map(([key, value]) => {
                if (!value) return null;

                switch (key) {
                  case "badge":
                    return `${value} badge`;
                  case "priorityDelivery":
                    return "Priority DM delivery";
                  case "advancedSearch":
                    return "Advanced Search";
                  case "dailyBoosts":
                    return `${value} daily DM boost${
                      Number(value) > 1 ? "s" : ""
                    }`;
                  case "profileHighlight":
                    return "Profile Highlight";
                  case "conciergeMatching":
                    return "Concierge Matching";
                  default:
                    return null;
                }
              })
              .filter(Boolean)
              .map((perk, index) => (
                <li key={index} className={classes.benefitItem}>
                  <span className={`${classes.benefitIcon} planName`}>✓</span>
                  <span className={`${classes.benefitText} planName`}>
                    {perk}
                  </span>
                </li>
              ))}
      </ul>
    </Box>
  );
};

export default SubscriptionPlanCard;
